import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Button, LinearProgress, Alert, Fade, useMediaQuery } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useTenantTheme } from '../tenant/TenantThemeProvider';
import { useToast } from '../ui/ToastProvider';
import { getCaptureRule } from '../config/captureQuality';
import { preloadModelAssets } from '../utils/modelLoader';

import {
  FrenteSilhouette,
  TraseroSilhouette,
  PerfilIzquierdoSilhouette,
  PerfilDerechoSilhouette,
  MotorSilhouette,
  VolanteSilhouette
} from './VehicleSilhouettes';

import perfilIzquierdo from '../images/perfilizquierdo.png';
import trasero from '../images/trasero.png';
import frente from '../images/frente.png';
import perfilDerecho from '../images/perfilderecho.png';
import volante from '../images/volante.png';
import chasis from '../images/chasis.png';
import documento from '../images/documento.png';
import motor from '../images/Motor.png';
import odometro from '../images/odometro.png';
import panel from '../images/panel.png';
import rueda from '../images/rueda.png';

const initialPhotoTypes = [
  { title: 'Foto Frontal', img: frente, category: 'Exterior', Silhouette: FrenteSilhouette },
  { title: 'Vista Posterior', img: trasero, category: 'Exterior', Silhouette: TraseroSilhouette },
  { title: 'Perfil Izquierdo', img: perfilIzquierdo, category: 'Exterior', Silhouette: PerfilIzquierdoSilhouette },
  { title: 'Perfil Derecho', img: perfilDerecho, category: 'Exterior', Silhouette: PerfilDerechoSilhouette },
  { title: 'Motor', img: motor, category: 'Mecánica', Silhouette: MotorSilhouette },
  { title: 'Volante', img: volante, category: 'Interior', Silhouette: VolanteSilhouette },
  { title: 'Panel', img: panel, category: 'Interior', Silhouette: null },
  { title: 'Kilometraje', img: odometro, category: 'Interior', Silhouette: null },
  { title: 'Rueda Repuesto', img: rueda, category: 'Seguridad', Silhouette: null },
  { title: 'Nº de Chasis', img: chasis, category: 'Identificación', Silhouette: null },
  { title: 'Documento', img: documento, category: 'Documentación', Silhouette: null }
];

const isAutoDetectionEnabled = (photoType) => {
  return ['0', '1', '2', '3'].includes(photoType.toString());
};

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

const PhotoChecklistMain = ({ photos, onSavePhoto }) => {
  const { brandColors } = useTenantTheme();
  const { showError, showSuccess } = useToast();
  const history = useHistory();
  const fileInputRef = useRef(null);
  const filmstripRef = useRef(null);
  const isLandscape = useMediaQuery('(orientation: landscape)');

  const [uploadError, setUploadError] = useState('');

  const firstMissingStep = photos.findIndex(p => p === null || p === undefined);
  const allPhotosTaken = firstMissingStep === -1;
  const initialActive = allPhotosTaken ? photos.length - 1 : firstMissingStep;

  const [selectedStep, setSelectedStep] = useState(initialActive);

  useEffect(() => {
    const missing = photos.findIndex(p => p === null || p === undefined);
    if (missing !== -1) {
      setSelectedStep(missing);
    } else {
      setSelectedStep(photos.length - 1);
    }
  }, [photos]);

  useEffect(() => {
    if (filmstripRef.current) {
      const activeEl = filmstripRef.current.children[selectedStep];
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedStep]);

  useEffect(() => {
    preloadModelAssets('angulos');
  }, []);

  const handleCapture = () => {
    setUploadError('');
    if (selectedStep !== 10) {
      history.push(`/camera/${selectedStep}`);
    }
  };

  const handleCameraCaptureDocument = () => {
    setUploadError('');
    history.push('/camera/10');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Tipo de archivo no permitido. Solo imágenes.');
        showError('Solo se permiten imágenes para este paso.');
        return;
      }
      if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        setUploadError('Archivo excede máximo de 10MB.');
        showError('La imagen excede el límite de 10MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        if (typeof onSavePhoto === 'function') {
          onSavePhoto(selectedStep, base64Image);
          showSuccess('Imagen cargada correctamente.');
        } else {
          setUploadError('Error interno. Inténtalo de nuevo.');
          showError('Error interno. Inténtalo de nuevo.');
        }
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const completedPhotosCount = photos.filter(p => p !== null && p !== undefined).length;
  const progressPct = (completedPhotosCount / initialPhotoTypes.length) * 100;

  const currentType = initialPhotoTypes[selectedStep];
  const isCompleted = !!photos[selectedStep];
  const rule = getCaptureRule(selectedStep);
  const autoDetect = isAutoDetectionEnabled(selectedStep);

  return (
    <Box sx={{
      height: '100dvh',
      minHeight: '100vh',
      backgroundColor: brandColors.background,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: '"Inter", "Roboto", sans-serif'
    }}>

      {/* ── MAIN CONTENT: Split View ── */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: isLandscape ? 'row' : 'column', md: 'row' },
        minHeight: 0,
        overflow: 'hidden'
      }}>

        {/* LEFT: Image Viewer */}
        <Box sx={{
          flex: { xs: isLandscape ? 1 : 'none', md: 1.6 },
          height: { xs: isLandscape ? '100%' : '45%', md: '100%' },
          position: 'relative',
          bgcolor: '#0a0a0a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <Fade in={true} key={selectedStep}>
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {photos[selectedStep] ? (
                <img
                  src={photos[selectedStep]}
                  alt={currentType.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'opacity 0.3s ease'
                  }}
                />
              ) : (
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  background: `radial-gradient(circle, ${brandColors.cardBackground} 0%, #000000 100%)`,
                  position: 'relative'
                }}>
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    position: 'absolute', width: '100%', height: '100%'
                  }}>
                    {currentType.Silhouette ? (
                      <Box sx={{ 
                        width: '100%', 
                        height: '100%', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.8,
                        filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.2))'
                      }}>
                        <currentType.Silhouette color={brandColors.primary} size={isLandscape ? "55%" : "70%"} animate={false} />
                      </Box>
                    ) : (
                      <img
                        src={currentType.img}
                        alt={currentType.title}
                        style={{
                          width: isLandscape ? '60%' : '75%',
                          height: isLandscape ? '60%' : '75%',
                          objectFit: 'contain',
                          opacity: 0.5,
                          filter: 'grayscale(100%) contrast(1.5)'
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{
                    width: '100%',
                    position: 'absolute',
                    bottom: isLandscape ? 15 : '8%',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    zIndex: 2
                  }}>
                    <Typography variant="body1" sx={{
                      color: 'white', fontWeight: 600, opacity: 0.8,
                      letterSpacing: 2, textTransform: 'uppercase', fontSize: '0.85rem',
                      textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(0,0,0,0.5)',
                      backgroundColor: 'rgba(0,0,0,0.4)',
                      padding: '4px 12px',
                      borderRadius: '12px'
                    }}>
                      Referencia Visual
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>

          {/* Success Badge */}
          {photos[selectedStep] && (
            <Box sx={{
              position: 'absolute', top: 12, right: 12,
              bgcolor: brandColors.successGreen || '#10b981', color: 'white',
              display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.4, borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)', fontSize: '0.75rem', fontWeight: 700
            }}>
              <CheckCircleIcon sx={{ fontSize: 16 }} /> Capturada
            </Box>
          )}

          {/* Step Counter Overlay */}
          <Box sx={{
            position: 'absolute', top: 12, left: 12,
            bgcolor: 'rgba(0,0,0,0.6)', color: 'white',
            px: 1.5, py: 0.4, borderRadius: 2,
            fontSize: '0.75rem', fontWeight: 700, backdropFilter: 'blur(4px)'
          }}>
            {selectedStep + 1} / {initialPhotoTypes.length}
          </Box>
        </Box>

        {/* RIGHT: Info Panel */}
        <Box sx={{
          width: { xs: isLandscape ? '45%' : '100%', md: '320px' },
          minWidth: { xs: isLandscape ? '250px' : '100%', md: '280px' },
          maxWidth: { xs: isLandscape ? '350px' : '100%', md: '360px' },
          flex: { xs: isLandscape ? 'none' : 1, md: 'none' },
          height: { xs: isLandscape ? '100%' : 'auto', md: '100%' },
          bgcolor: brandColors.cardBackground,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderLeft: { xs: isLandscape ? `1px solid ${brandColors.borderColor}22` : 'none', md: `1px solid ${brandColors.borderColor}22` },
          borderTop: { xs: isLandscape ? 'none' : `1px solid ${brandColors.borderColor}22`, md: 'none' }
        }}>
          {/* Header with progress */}
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: { xs: 1, md: 2 }, pb: { xs: 1, md: 1.5 }, borderBottom: `1px solid ${brandColors.borderColor}22` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: brandColors.text, opacity: 0.6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                Inspección Vehicular
              </Typography>
              <Typography variant="caption" sx={{ color: brandColors.primary, fontWeight: 700 }}>
                {completedPhotosCount}/{initialPhotoTypes.length}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: `${brandColors.primary}22`,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: brandColors.successGreen || brandColors.primary,
                  borderRadius: 2
                }
              }}
            />
          </Box>

          {/* Step Info */}
          <Box sx={{ flex: 1, minHeight: 0, px: { xs: 1.5, md: 2.5 }, py: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', overflowY: 'auto', pb: { xs: 1.5, md: 2 } }}>
            <Typography variant="overline" sx={{
              color: brandColors.primary, fontWeight: 800, letterSpacing: 1.5, fontSize: '0.65rem', lineHeight: 1
            }}>
              {currentType.category}
            </Typography>
            <Typography variant="h6" sx={{ color: brandColors.text, fontWeight: 800, lineHeight: 1.2, mb: 1, mt: 0.5, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {currentType.title}
            </Typography>

            {/* Instruction Box */}
            <Box sx={{
              p: 1.2, bgcolor: `${brandColors.primary}0A`,
              borderRadius: 2, borderLeft: `3px solid ${brandColors.primary}`,
              mb: 1.5
            }}>
              <Typography variant="body2" sx={{ color: brandColors.darkText, fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.4 }}>
                {rule?.instruction || 'Sigue las marcas en pantalla para una captura correcta.'}
              </Typography>
              {autoDetect && (
                <Typography variant="caption" sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  color: '#10b981', mt: 0.75, fontWeight: 700, fontSize: '0.72rem', lineHeight: 1.3, flexWrap: 'wrap'
                }}>
                  <AutoAwesomeIcon sx={{ fontSize: 14 }} /> IA Activada
                </Typography>
              )}
            </Box>

            {uploadError && (
              <Alert severity="error" sx={{ mb: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>{uploadError}</Alert>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: { xs: 'max(12px, env(safe-area-inset-bottom))', md: 2 }, pt: 1, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, borderTop: `1px solid ${brandColors.borderColor}22`, backgroundColor: brandColors.cardBackground }}>
            <input
              type="file" ref={fileInputRef} style={{ display: 'none' }}
              onChange={handleFileUpload} accept="image/*"
            />

            {allPhotosTaken && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => history.push('/process-images')}
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  bgcolor: brandColors.successGreen || '#10b981',
                  color: 'white',
                  py: 1.5, borderRadius: 3, fontWeight: 700, fontSize: '0.85rem',
                  boxShadow: `0 4px 14px ${brandColors.successGreen || '#10b981'}55`,
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Procesar Imágenes
              </Button>
            )}

            {selectedStep === 10 ? (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  startIcon={<FileUploadIcon />}
                  sx={{
                    borderColor: brandColors.primary,
                    color: brandColors.primary,
                    py: 1.2, borderRadius: 3, fontWeight: 700,
                    '&:hover': { bgcolor: `${brandColors.primary}0A` }
                  }}
                >
                  Subir Archivo
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCameraCaptureDocument}
                  startIcon={<CameraAltIcon />}
                  sx={{
                    bgcolor: brandColors.primary, color: brandColors.onPrimary,
                    py: 1.2, borderRadius: 3, fontWeight: 700,
                    boxShadow: `0 4px 14px ${brandColors.primary}44`
                  }}
                >
                  Usar Cámara
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  startIcon={<FileUploadIcon />}
                  sx={{
                    borderColor: brandColors.primary,
                    color: brandColors.primary,
                    py: 1.2, borderRadius: 3, fontWeight: 700,
                    '&:hover': { bgcolor: `${brandColors.primary}0A` }
                  }}
                >
                  Subir Archivo
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCapture}
                  startIcon={<CameraAltIcon />}
                  sx={{
                    bgcolor: brandColors.primary, color: brandColors.onPrimary,
                    py: 1.2, borderRadius: 3, fontWeight: 700,
                    boxShadow: `0 4px 14px ${brandColors.primary}44`
                  }}
                >
                  {isCompleted ? 'Retomar Foto' : 'Capturar'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── BOTTOM FILMSTRIP ── */}
      <Box sx={{
        flexShrink: 0,
        height: { xs: '82px', md: '88px' },
        bgcolor: brandColors.cardBackground,
        borderTop: `1px solid ${brandColors.borderColor}22`,
        display: 'flex',
        alignItems: 'center',
        px: 1.5
      }}>
        <Box
          ref={filmstripRef}
          sx={{
            display: 'flex', gap: 1, overflowX: 'auto', flex: 1, alignItems: 'center',
            py: 0.5,
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: `${brandColors.borderColor}88`, borderRadius: '4px' }
          }}
        >
          {initialPhotoTypes.map((type, idx) => {
            const hasPhoto = !!photos[idx];
            const isSelected = selectedStep === idx;
            const isMissing = !hasPhoto;
            const isPendentButTurn = firstMissingStep === idx;
            const isLocked = isMissing && !isPendentButTurn;

            return (
              <Box
                key={idx}
                onClick={isLocked ? undefined : () => setSelectedStep(idx)}
                sx={{
                  position: 'relative',
                  width: '76px',
                  minWidth: '76px',
                  height: '56px',
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  border: isSelected ? `2px solid ${brandColors.primary}` : '2px solid transparent',
                  boxShadow: isSelected ? `0 0 0 1px ${brandColors.primary}44` : 'none',
                  opacity: isLocked ? 0.35 : 1,
                  transition: 'all 0.2s ease',
                  transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                  bgcolor: '#1a1a1a', // Fondo oscuro para miniatura
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': !isLocked ? { borderColor: `${brandColors.primary}88` } : {}
                }}
              >
                {hasPhoto ? (
                  <img
                    src={photos[idx]}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : type.Silhouette ? (
                    <Box sx={{ width: '60%', height: '60%', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <type.Silhouette color={brandColors.primary} size="100%" animate={false} />
                    </Box>
                  ) : (
                  <img
                    src={type.img}
                    alt=""
                    loading="lazy"
                    style={{ width: '70%', height: '70%', objectFit: 'contain', opacity: 0.5, filter: 'grayscale(100%)' }}
                  />
                )}

                {hasPhoto && (
                  <Box sx={{
                    position: 'absolute', top: 2, right: 2,
                    bgcolor: 'white', borderRadius: '50%',
                    width: 14, height: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                  </Box>
                )}

                {isLocked && (
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <LockIcon sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                )}

                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  bgcolor: 'rgba(0,0,0,0.7)', color: 'white',
                  px: 0.5, py: 0.15, fontSize: '0.55rem', fontWeight: 700,
                  textAlign: 'center', lineHeight: 1.2,
                  backdropFilter: 'blur(2px)'
                }}>
                  {type.title}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default PhotoChecklistMain;
